import {
  ChangeDetectionStrategy,
  Component,
  Input,
  EventEmitter,
  Output,
  Inject,
  PLATFORM_ID,
  ViewChild,
   OnInit,
   ChangeDetectorRef,
   TemplateRef,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CameraDialogComponent } from '../camera-dialog/camera-dialog.component';
import { isPlatformBrowser } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { ComponentDefinition } from '@formbird/types';
import { FormbirdInjectorService } from '@formbird/services';
import { ConfigService } from "formbird-services";
interface videoInput extends ComponentDefinition {
  value: string;
  viewValue: string;
}
@Component({
  selector: 'fb-upload-file',
  templateUrl: './fb-upload-file.component.html',
  styleUrls: ['./fb-upload-file.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbUploadFileComponent implements OnInit {
  @Output() onFileSelect: EventEmitter<any> = new EventEmitter();
  @Input() disabled: boolean;
  @Input() fieldName: boolean;
  @Input() statusNote: string;
  @Input() acceptedFiles: string[];
  @ViewChild('button') testButton!: MatButton;
  @ViewChild('camera') cameraInput: HTMLInputElement;
  @ViewChild('cameraButton') cameraButton: MatButton;
  @ViewChild('permissionGrantedDialog') permissionGrantedDialog: TemplateRef<any>
  permissionGrantedDialogRef:MatDialogRef<any>
  videoInputs: videoInput[];
  notificationService: any;
  isMobile = false;
  hasPermission ='prompt';
  stream!: MediaStream;
  cameraButtonClicked = false;

  defaultExtensionWhiteList = [
              'json',
              'jpeg',
              'jpg',
              'png',
              'tiff',
              'tif',
              'pdf',
              'bmp',
              'gif',
              'txt',
              'docx',
              'doc',
              'ppt',
              'pptx',
              'xlsx',
              'xls',
              'zip',
              'rar',
              '7zip',
              'gzip',
              'ico',
              'svg'
  ];
  extensionWhiteList: string[];
  extensionListString: string;

  constructor(
    public dialog: MatDialog,
    private cd: ChangeDetectorRef,
    protected formbirdInjectorService: FormbirdInjectorService,
    private configService: ConfigService,
    @Inject(PLATFORM_ID) private _platform: Object
  ) {
    this.notificationService = this.formbirdInjectorService.get('NotificationService')
  }


  ngOnInit(): void {
    this.isCaptureAttributeSupported();

    this.extensionWhiteList = this.acceptedFiles ?? this.configService.clientConfig().fileUploader?.extensionWhitelist ?? this.defaultExtensionWhiteList;
    this.extensionListString = this.extensionWhiteList.map(ext => `.${ext}`).join(',');
  }

  onFileSelected(evt) {
    this.onFileSelect.emit(evt.target.files || []);
    evt.target.value = null;
  }


  async requestPermission() {
    if (isPlatformBrowser(this._platform) && 'mediaDevices' in navigator) {
       try {
        if(this.hasPermission == 'prompt'){
         await this.promptForCameraAccess()
        }else if(this.hasPermission == 'granted') {
         this.openCamera()
        }else if(this.hasPermission == 'denied'){
          throw new PermissionError("Don't have permissions")
        }
      } catch (err: any) {
        if (err.name == 'NotAllowedError') {
          this.notificationService.error(
            'Unable to access your camera. Please give your browser access to camera and try again!'
          );
        } else {
          this.notificationService.error(err);
        }
      }
    } else {
      const errorMessage = 'Current browser is not supported';
      this.notificationService.error(errorMessage);
    }
  }


  async promptForCameraAccess() {
    try {
      let stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      stream.getTracks().forEach((track) => track.stop());
      this.hasPermission = 'granted';
      this.openCamera()
      this.cd.detectChanges();
      return Promise.resolve(true);
    } catch (error: any) {
      this.hasPermission = 'denied';
      this.cd.detectChanges();
      return Promise.reject(error);
    }
  }


  async openCamera() {
    this.videoInputs = await this.emulatedDevices();
    if (this.isMobile) {
      this.permissionGrantedDialogRef = this.dialog.open(
        this.permissionGrantedDialog
      );
      document
        .getElementById('continueButton')
        ?.addEventListener('click', () => {
          this.permissionGrantedDialog.elementRef.nativeElement.close();
        });
      this.permissionGrantedDialogRef.beforeClosed().subscribe(() => {
        document
          .getElementById('continueButton')
          ?.removeEventListener('click', () => {
            this.permissionGrantedDialog.elementRef.nativeElement.close();
          });
      });
    } else {
      this.openDialog();
    }
  }
  async emulatedDevices() {
    if (!navigator.mediaDevices?.enumerateDevices) {
      console.log('enumerateDevices() not supported.');
      return Promise.reject();
    } else {
      try {
        let devices = await navigator.mediaDevices.enumerateDevices();
        let videoInputs: videoInput[] = [];
        devices.forEach((device) => {
          // List cameras .
          if (device.kind == 'videoinput' && device.label) {
            videoInputs.push({
              value: device.deviceId,
              viewValue: device.label,
            });
          }
        });
        return Promise.resolve(videoInputs);
      } catch (err) {
        return Promise.reject(err);
      }
    }
  }

  isCaptureAttributeSupported() {
    let input = document.createElement('input');
    if ('capture' in input) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  openDialog() {
    let dialogRef = this.dialog.open(CameraDialogComponent, {
      data: { videoInputs: this.videoInputs },
    });
    dialogRef.afterClosed().subscribe((photo) => {
      if (photo) {
        this.onFileSelect.emit([photo])
       }
    });
  }
  fileDropped(files) {
    if (!this.disabled) {
      this.onFileSelect.emit(files || []);
    }
  }

  onPaste($event: ClipboardEvent) {
    const files = $event.clipboardData?.files;
    if (files && !this.disabled) {
      this.onFileSelect.emit(files || []);
    }
  }
}
export class Error {
  message:string
  name:string
  constructor(message:string) {
    this.message = message;
    this.name = "Error";
   }
}

export class PermissionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotAllowedError";
  }
}
